import React, { useState, useEffect } from "react";
import { IoCheckmarkCircle, IoClose, IoWarning } from "react-icons/io5";
import { useMutation, useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import MpesaLogo from "../../assets/img/ecommerce/Mpesa.png";
import WaafiPayLogo from "../../assets/img/ecommerce/WaafiPay.png";
import { subscribeAndPay, listenToPaymentStatus, validateReferralCode, getUserProfile } from "api";
import AnimatedCheckmark from "components/dataDisplay/AnimatedCheckmark";
import { parsePaymentError, showPaymentError, isRetryableError, startCooldown, isInCooldown, getCooldownRemaining, clearCooldown } from "../../utils/errorHandler";

const Spinner = () => (
  <div className="inline-block w-5 h-5 border-4 border-t-orange-500 border-gray-200 rounded-full animate-spin"></div>
);

// Number formatter for currencies
const formatCurrency = (amount, currency, country) => {
  const locale = country === "Kenya" ? "en-KE" : country === "Somalia" ? "en-SO" : "en-KE";
  return new Intl.NumberFormat(locale, {
    style: "decimal",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatUSD = (amount) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
};

const SubscriptionModal = ({ onClose, parentName, courseId, userId, mentorId, country: initialCountry = "", isRenewal = false }) => {
  const [subscriptionType, setSubscriptionType] = useState("monthly");
  const [referralCode, setReferralCode] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [country, setCountry] = useState(initialCountry);
  const [subscriptionSuccess, setSubscriptionSuccess] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [transactionId, setTransactionId] = useState(null);
  const [billingId, setBillingId] = useState(null);
  
  // Cooldown state for real-time updates
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [isInCooldownState, setIsInCooldownState] = useState(false);

  const [originalAmount, setOriginalAmount] = useState(1300); // KES for Kenya, SOS for Somalia
  const [discountedAmount, setDiscountedAmount] = useState(1300); // KES or SOS
  const [finalAmount, setFinalAmount] = useState(1300); // KES or SOS, rounded
  const [originalAmountUSD, setOriginalAmountUSD] = useState(10); // USD
  const [discountedAmountUSD, setDiscountedAmountUSD] = useState(10); // USD
  const [exchangeRateKES, setExchangeRateKES] = useState(130); // USD to KES
  const [exchangeRateSOS, setExchangeRateSOS] = useState(575); // USD to SOS
  const [discountApplied, setDiscountApplied] = useState(false);
  const [schoolSponsored, setSchoolSponsored] = useState(false);
  const [referralVisible, setReferralVisible] = useState(false);
  const [appliedReferralCode, setAppliedReferralCode] = useState(null);

  // Fetch user profile with useQuery
  const { data: profile, isLoading: profileLoading, isError: profileError } = useQuery({
    queryKey: ["userProfile", userId],
    queryFn: async () => {
      const response = await getUserProfile();
      console.log("useQuery: Fetched user profile:", JSON.stringify(response, null, 2));
      return response;
    },
    onSuccess: (data) => {
      setPhoneNumber(data.user.phone || "");
      // Autoselect country: Kenya or Somalia, default to initialCountry or Kenya if invalid
      const userCountry = data.user.country;
      const validCountry = ["Kenya", "Somalia"].includes(userCountry) ? userCountry : 
                      ["Kenya", "Somalia"].includes(initialCountry) ? initialCountry : "Kenya";
      setCountry(validCountry);
    },
    onError: (error) => {
      console.error("useQuery: Error fetching user profile:", error);
      setErrorMessage("Failed to load profile. Please select your country and enter your phone number.");
      setCountry("Kenya"); // Default to Kenya on error
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Fetch exchange rates for KES and SOS
  useEffect(() => {
    const fetchExchangeRates = async () => {
      try {
        const response = await fetch(`https://api.exchangerate-api.com/v4/latest/USD`);
        const data = await response.json();
        setExchangeRateKES(data.rates.KES || 130);
        setExchangeRateSOS(data.rates.SOS || 575);
      } catch (error) {
        console.error("Error fetching exchange rates:", error);
        setExchangeRateKES(130);
        setExchangeRateSOS(575);
      }
    };
    fetchExchangeRates();
  }, []);

  // Set amounts based on subscription type and country
  useEffect(() => {
    const baseAmountUSD = subscriptionType === "yearly" ? 15 * 12 : 15;
    const discountUSD = subscriptionType === "yearly" ? baseAmountUSD * 0.7 : baseAmountUSD;

    if (country === "Kenya") {
      const baseAmountKES = Math.round(baseAmountUSD * exchangeRateKES);
      const discountKES = Math.round(discountUSD * exchangeRateKES);
      setOriginalAmount(baseAmountKES);
      setDiscountedAmount(discountKES);
      setFinalAmount(discountKES);
    } else if (country === "Somalia") {
      const baseAmountSOS = Math.round(baseAmountUSD * exchangeRateSOS);
      const discountSOS = Math.round(discountUSD * exchangeRateSOS);
      setOriginalAmount(baseAmountSOS);
      setDiscountedAmount(discountSOS);
      setFinalAmount(discountSOS);
    } else {
      const baseAmountKES = Math.round(baseAmountUSD * exchangeRateKES);
      const discountKES = Math.round(discountUSD * exchangeRateKES);
      setOriginalAmount(baseAmountKES);
      setDiscountedAmount(discountKES);
      setFinalAmount(discountKES);
    }

    setOriginalAmountUSD(baseAmountUSD);
    setDiscountedAmountUSD(discountUSD);
    setSchoolSponsored(false);
    setDiscountApplied(false);
  }, [subscriptionType, exchangeRateKES, exchangeRateSOS, country]);

  // Real-time cooldown updates for WaafiPay
  useEffect(() => {
    if (country !== "Somalia") return;

    const interval = setInterval(() => {
      const remaining = getCooldownRemaining(userId);
      const inCooldown = isInCooldown(userId);
      
      setCooldownRemaining(remaining);
      setIsInCooldownState(inCooldown);
    }, 1000);

    return () => clearInterval(interval);
  }, [country, userId]);

  // Socket.io listeners for payment events
  useEffect(() => {
    if (!billingId) return;

    const cleanup = listenToPaymentStatus((data) => {
      if (data.billingId === billingId && data.userId === userId) {
        setIsProcessing(false);
        if (data.status === "PAID") {
          setSubscriptionSuccess(true);
          setErrorMessage("");
        } else if (data.status === "FAILED") {
          setErrorMessage(`Payment failed: ${data.message || "Please try again."}`);
        }
      }
    });

    // STK push timeout (2 minutes)
    const timer = setTimeout(() => {
      if (isProcessing) {
        setIsProcessing(false);
        setErrorMessage("Payment timed out. Please try again.");
      }
    }, 120 * 1000);

    return () => {
      cleanup();
      clearTimeout(timer);
    };
  }, [billingId, userId, isProcessing]);

  const skipPayment = Number(discountedAmount) === 0;

  // Mutation to validate the referral code
  const referralMutation = useMutation({
    mutationFn: async ({ referralCode, subscriptionType }) => {
      if (!referralCode) throw new Error("No referral code provided");
      const { discountedPrice, schoolSponsored, discountPercentage } = await validateReferralCode(
        referralCode,
        subscriptionType
      );
      return { discountedPrice, schoolSponsored, discountPercentage };
    },
    onSuccess: (data) => {
      // Calculate discounted amounts in local currency
      const baseAmountUSD = subscriptionType === "yearly" ? 15 * 12 : 15;
      const discountUSD = (baseAmountUSD * data.discountPercentage) / 100;
      const discountedAmountUSD = baseAmountUSD - discountUSD;
      
      setDiscountedAmountUSD(discountedAmountUSD);
      setSchoolSponsored(data.schoolSponsored);
      setDiscountApplied(true);
      setErrorMessage("");
      
      // Store referral code info for display
      setAppliedReferralCode({
        code: referralCode,
        discount: data.discountPercentage,
        originalAmount: baseAmountUSD,
        discountedAmount: discountedAmountUSD,
      });
    },
    onError: (error) => {
      setErrorMessage(error.message || "Failed to validate referral code");
    },
  });

  const handleApplyReferralCode = () => {
    if (!referralCode.trim()) {
      setErrorMessage("Please enter a referral code");
      return;
    }
    referralMutation.mutate({ referralCode, subscriptionType });
  };

  const removeReferralCode = () => {
    setReferralCode("");
    setAppliedReferralCode(null);
    setDiscountApplied(false);
    setSchoolSponsored(false);
    // Reset to original pricing
    const baseAmountUSD = subscriptionType === "yearly" ? 15 * 12 : 15;
    setDiscountedAmountUSD(baseAmountUSD);
  };

  // Mutation to initiate payment
  const paymentMutation = useMutation({
    mutationFn: async () => {
      const phoneRegex = country === "Somalia" ? /^\+2526\d{8}$/ : /^\+2547\d{8}$/;
      if (!phoneNumber.match(phoneRegex)) {
        throw new Error(`Please enter a valid ${country === "Somalia" ? "Somali" : "Kenyan"} phone number (e.g., ${country === "Somalia" ? "+2526XXXXXXXX" : "+2547XXXXXXXX"}).`);
      }
      const response = await subscribeAndPay({
        userId,
        courseId,
        referralCode: referralCode || undefined,
        subscriptionType,
        phoneNumber,
        country,
      });
      return response;
    },
    onSuccess: (data) => {
      setBillingId(data.billingId);
      setTransactionId(data.transactionId || null);
      setIsProcessing(true);
      setErrorMessage("Please check your phone to confirm the payment with your PIN.");
    },
    onError: (error) => {
      // Only apply enhanced error handling for WaafiPay (Somalia)
      if (country === "Somalia") {
        const errorInfo = parsePaymentError(error);
        setErrorMessage(errorInfo.message);
        
        // Start cooldown for retryable WaafiPay errors
        if (errorInfo.requiresCooldown) {
          startCooldown(userId);
        }
        
        // Show detailed error toast for WaafiPay errors
        if (errorInfo.code && errorInfo.code !== 'UNKNOWN_ERROR') {
          showPaymentError(errorInfo, isRetryableError(error) ? handleRetry : null, userId);
        }
      } else {
        // Default error handling for M-Pesa
        setErrorMessage(error.message || "Error initiating payment. Please try again.");
      }
      
      setIsProcessing(false);
    },
  });

  // Handle payment success
  const handlePaymentSuccess = async (data) => {
    setSubscriptionSuccess(true);
    // Clear cooldown when WaafiPay payment succeeds
    if (country === "Somalia") {
      clearCooldown(userId);
    }
  };

  const handleConfirmSubscription = () => {
    if (isProcessing || (!country && !profileLoading)) return;
    
    // Both Kenya (M-Pesa) and Somalia (WaafiPay) use the same paymentMutation
    // The mutation handles the country-specific logic internally
    paymentMutation.mutate();
  };

  // Retry payment
  const handleRetry = () => {
    // Check if user is in cooldown for WaafiPay
    if (country === "Somalia" && isInCooldownState) {
      return; // Don't allow retry if in cooldown
    }
    
    setErrorMessage("");
    setIsProcessing(false);
    setBillingId(null);
    setTransactionId(null);
  };

  const paymentProvider = country === "Somalia" ? "WaafiPay" : "M-Pesa";
  const currency = country === "Kenya" ? "KES" : country === "Somalia" ? "SOS" : "KES";
  const logo = country === "Somalia" ? WaafiPayLogo : MpesaLogo;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-md p-4">
      <motion.div
        className="relative bg-white dark:bg-navy-900 rounded-2xl shadow-2xl w-full max-w-md p-6 sm:p-8"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white transition-colors"
        >
          <IoClose size={28} />
        </button>
        <h2 className="text-2xl sm:text-3xl font-bold text-center text-navy-700 dark:text-white mb-6">
          Subscribe to {parentName}
        </h2>

        <AnimatePresence>
          {subscriptionSuccess ? (
            <motion.div
              className="flex flex-col items-center mt-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <AnimatedCheckmark />
              <p className="mt-4 text-center text-gray-600 dark:text-gray-300 text-lg">
                Subscription successful! Your transaction is under review.
              </p>
              <button
                onClick={onClose}
                className="w-full mt-6 py-3 px-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg font-medium hover:from-orange-600 hover:to-orange-700 transition-all dark:from-navy-800 dark:to-navy-700 dark:hover:from-navy-700 dark:hover:to-navy-600"
              >
                Close
              </button>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {profileLoading ? (
                <div className="flex justify-center items-center">
                  <Spinner />
                </div>
              ) : (
                <>
                  {!profileLoading && !country && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-navy-700 dark:text-white mb-3">
                        Select Your Country
                      </h3>
                      <select
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                        className="w-full p-3 border border-gray-300 dark:border-navy-700 rounded-lg bg-white dark:bg-navy-900 text-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500 transition"
                      >
                        <option value="">Select a country</option>
                        <option value="Kenya">Kenya</option>
                        <option value="Somalia">Somalia</option>
                      </select>
                    </div>
                  )}

                  <div className="flex justify-center gap-4 mb-6">
                    {["monthly", "yearly"].map((type) => (
                      <button
                        key={type}
                        className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                          subscriptionType === type
                            ? "bg-orange-500 text-white shadow-md"
                            : "bg-gray-100 dark:bg-navy-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-navy-700"
                        }`}
                        onClick={() => setSubscriptionType(type)}
                      >
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </button>
                    ))}
                  </div>

                  <div className="mb-6 flex justify-between items-center">
                    {subscriptionType === "yearly" && (
                      <p className="text-gray-400 dark:text-gray-500 line-through text-sm sm:text-base">
                        {currency} {formatCurrency(originalAmount, currency, country)} (~{formatUSD(originalAmountUSD)})
                      </p>
                    )}
                    <motion.div
                      className="text-3xl sm:text-4xl font-extrabold text-navy-700 dark:text-white"
                      initial={{ scale: 0.95 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.2 }}
                    >
                      {currency} {formatCurrency(finalAmount, currency, country)} <span className="text-sm font-normal">~{formatUSD(discountedAmountUSD)}</span>
                    </motion.div>
                  </div>

                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-navy-700 dark:text-white mb-3">
                      Payment Details
                    </h3>
                    {country === "Kenya" ? (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Phone Number
                          </label>
                          <input
                            type="tel"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            placeholder="+2547XXXXXXXX"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            disabled={isProcessing}
                          />
                        </div>
                        
                        {/* M-Pesa STK Push info - no reference number needed */}
                        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                          <div className="flex items-start">
                            <div className="flex-shrink-0">
                              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div className="ml-3">
                              <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                                M-Pesa STK Push
                              </h3>
                              <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                                <p>You will receive an M-Pesa prompt on your phone to complete the payment.</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // Somalia (WaafiPay) - existing form
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Phone Number
                          </label>
                          <input
                            type="tel"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            placeholder="+2526XXXXXXXX"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            disabled={isProcessing}
                          />
                        </div>
                      </div>
                    )}
                    <AnimatePresence>
                      {isProcessing && (
                        <motion.p
                          className="mt-3 text-gray-600 dark:text-gray-300 flex items-center gap-2"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                        >
                          <Spinner />
                          Check your phone to confirm the payment with your {paymentProvider} PIN.
                        </motion.p>
                      )}
                      {errorMessage && (
                        <motion.div
                          className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                        >
                          <div className="flex items-start gap-2">
                            <IoWarning size={20} className="text-red-500 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-red-600 dark:text-red-400 text-sm font-medium">
                                {errorMessage}
                              </p>
                              {/* Enhanced error tips only for WaafiPay (Somalia) */}
                              {country === "Somalia" && (
                                <>
                                  {errorMessage.includes("Insufficient funds") && (
                                    <p className="text-red-500 dark:text-red-300 text-xs mt-1">
                                      💡 Add money to your WaafiPay account and try again
                                    </p>
                                  )}
                                  {errorMessage.includes("Invalid phone number") && (
                                    <p className="text-red-500 dark:text-red-300 text-xs mt-1">
                                      💡 Verify your phone number is registered with WaafiPay
                                    </p>
                                  )}
                                  {errorMessage.includes("Network") && (
                                    <p className="text-red-500 dark:text-red-300 text-xs mt-1">
                                      💡 Check your internet connection and try again
                                    </p>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  <div className="mb-6">
                    <button
                      className="text-orange-500 dark:text-orange-400 hover:underline text-sm font-medium"
                      onClick={() => setReferralVisible(!referralVisible)}
                    >
                      {referralVisible ? "Hide Referral Code" : "Have a referral code?"}
                    </button>
                    <AnimatePresence>
                      {referralVisible && (
                        <motion.div
                          className="mt-3 space-y-3"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={referralCode}
                              onChange={(e) => setReferralCode(e.target.value)}
                              placeholder="Enter referral code"
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                              disabled={isProcessing}
                            />
                            <button
                              onClick={handleApplyReferralCode}
                              disabled={isProcessing || !referralCode.trim()}
                              className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {referralMutation.isLoading ? <Spinner /> : "Apply Code"}
                            </button>
                          </div>
                          {appliedReferralCode && (
                            <button
                              onClick={removeReferralCode}
                              className="text-sm text-red-500 hover:text-red-700"
                            >
                              Remove Code
                            </button>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {appliedReferralCode && (
                    <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <IoCheckmarkCircle className="text-green-500" size={20} />
                          <span className="text-green-700 dark:text-green-300 font-medium">
                            {appliedReferralCode.discount}% OFF
                          </span>
                        </div>
                        <div className="text-sm text-green-600 dark:text-green-400">
                          Code: {appliedReferralCode.code}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-3">
                    <button
                      onClick={handleConfirmSubscription}
                      disabled={isProcessing || !phoneNumber || !country || (country === "Somalia" && isInCooldownState)}
                      className={`w-full py-3 px-4 text-white rounded-lg font-medium transition-all ${
                        isProcessing || !phoneNumber || !country || (country === "Somalia" && isInCooldownState)
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 shadow-md"
                      }`}
                    >
                      {isProcessing ? (
                        <span className="flex items-center justify-center gap-2">
                          <Spinner />
                          Processing...
                        </span>
                      ) : country === "Somalia" && isInCooldownState ? (
                        `Wait ${cooldownRemaining}s`
                      ) : skipPayment ? (
                        "Subscribe"
                      ) : (
                        "Pay Now"
                      )}
                    </button>
                    {errorMessage && !isProcessing && (
                      <button
                        onClick={handleRetry}
                        disabled={country === "Somalia" && isInCooldownState}
                        className={`w-full py-3 px-4 rounded-lg font-medium transition ${
                          country === "Somalia" && isInCooldownState
                            ? "bg-gray-100 dark:bg-navy-900 text-gray-400 cursor-not-allowed"
                            : "bg-gray-200 dark:bg-navy-800 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-navy-700"
                        }`}
                      >
                        {country === "Somalia" && isInCooldownState 
                          ? `Wait ${cooldownRemaining}s`
                          : country === "Somalia" && errorMessage.includes("Insufficient funds") 
                            ? "Add Funds & Retry" 
                            : "Retry Payment"
                        }
                      </button>
                    )}
                    <button
                      onClick={onClose}
                      className="w-full py-3 px-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition"
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default SubscriptionModal;